import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import NewAppointmentDialog from "../components/NewAppointmentDialog";
import LogVisitDialog from "../components/LogVisitDialog";
import api, { PRACTICE_TZ, tzDateKey, tzMidnight, tzHours, tzMinutes, tzWeekday } from "../lib/api";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Calendar as CalIcon, MoreVertical, Search, Plus } from "lucide-react";

const DAY_START_HOUR = 7;   // 7 am
const DAY_END_HOUR = 19;    // 7 pm
const HOUR_HEIGHT = 96;     // px per hour
const COLS_DAILY = 5;       // visual columns inside Daily view for layout

function startOf(date, mode) {
  // Use the practice's local timezone for day boundaries so the schedule
  // matches the practice wall clock (not the user's browser TZ or UTC).
  const ymd = tzDateKey(date);
  if (mode === "day") return tzMidnight(ymd);
  if (mode === "week") {
    const wd = tzWeekday(date); // 0=Sun..6=Sat in practice TZ
    const [y, m, d] = ymd.split("-").map(Number);
    // Step back `wd` days in UTC space, then re-anchor at practice midnight
    const sundayProbe = new Date(Date.UTC(y, m - 1, d - wd, 12)); // noon avoids DST edge
    return tzMidnight(tzDateKey(sundayProbe));
  }
  // month — first day of practice-local month
  const [y, m] = ymd.split("-").map(Number);
  return tzMidnight(`${y}-${String(m).padStart(2, "0")}-01`);
}

function pxFromMinutes(mins) {
  return (mins / 60) * HOUR_HEIGHT;
}

function eventStyleForStatus(status) {
  switch (status) {
    case "completed":
      return { bg: "bg-[#E2ECE3]", border: "border-[#5C8266]", text: "text-[#3E4F3A]", chip: "bg-white text-[#5C8266]" };
    case "checked_in":
    case "in_progress":
      // Filled style like the "In Progress" reference (Giovanni Verdi)
      return { bg: "bg-[#3E4F3A]", border: "border-[#1F2421]", text: "text-white", chip: "bg-white/20 text-white", filled: true };
    case "held":
    case "confirmed":
      return { bg: "bg-[#F4EAD2]", border: "border-[#B58A3E]", text: "text-[#1F2421]", chip: "bg-white text-[#B58A3E]" };
    case "canceled":
    case "no_show":
      return { bg: "bg-[#F4DEDE]", border: "border-[#B85C5C]", text: "text-[#1F2421]", chip: "bg-white text-[#B85C5C]" };
    default:
      return { bg: "bg-white", border: "border-hairline", text: "text-charcoal", chip: "bg-white text-slate" };
  }
}

function statusLabel(s) {
  return {
    completed: "Completed",
    checked_in: "In progress",
    in_progress: "In progress",
    held: "Holding",
    confirmed: "Confirmed",
    canceled: "Cancelled",
    no_show: "No-show",
  }[s] || s;
}

export default function Schedule({
  scopedPractitionerId = null,
  allowedTabs = ["day", "week", "month", "by_practitioner", "by_day"],
  title = "Appointments & Calendar",
  subtitle = "Manage appointments, schedule visits, and view availability.",
  showQuickAction = true,
  onEventClick = null,
  enableCellCreate = true,
}) {
  const [mode, setMode] = useState(allowedTabs[0] || "day");
  const [anchor, setAnchor] = useState(new Date());
  const [appts, setAppts] = useState([]);
  const [walkInServices, setWalkInServices] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [search, setSearch] = useState("");

  // New-appointment dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogStart, setDialogStart] = useState(null);
  const [dialogPractitionerId, setDialogPractitionerId] = useState("");

  // Log-visit / add-on dialog state (opens when clicking an event card)
  const [logOpenId, setLogOpenId] = useState(null);

  const openNewAppt = (start, practitionerId = "") => {
    setDialogStart(start || null);
    setDialogPractitionerId(practitionerId || "");
    setDialogOpen(true);
  };

  // Default handler: open Log Visit dialog. Callers may override with onEventClick prop.
  const handleEventClick = onEventClick || ((appt) => setLogOpenId(appt.id));

  const fetchAppts = () => {
    // For By-Practitioner, fetch the single anchor day (treat like "day" mode).
    // For By-Day-of-Week, fetch a full week.
    let baseMode = mode;
    if (mode === "by_practitioner") baseMode = "day";
    if (mode === "by_day") baseMode = "week";
    const from = startOf(anchor, baseMode);
    const to = new Date(from);
    if (baseMode === "day") to.setUTCDate(to.getUTCDate() + 1);
    else if (baseMode === "week") to.setUTCDate(to.getUTCDate() + 7);
    else to.setUTCMonth(to.getUTCMonth() + 1);
    const params = { date_from: from.toISOString(), date_to: to.toISOString() };
    if (scopedPractitionerId) params.practitioner_id = scopedPractitionerId;
    api.get("/appointments", { params })
      .then(({ data }) => setAppts(data))
      .catch(() => setAppts([]));
  };

  useEffect(() => { fetchAppts(); /* eslint-disable-next-line */ }, [mode, anchor]);
  useEffect(() => {
    api.get("/services").then(({ data }) => setWalkInServices(data.filter((s) => s.walk_in_only)));
    api.get("/practitioners").then(({ data }) => setPractitioners(data));
  }, []);

  const shift = (delta) => {
    const d = new Date(anchor);
    const isDayLike = mode === "day" || mode === "by_practitioner";
    if (isDayLike) d.setUTCDate(d.getUTCDate() + delta);
    else if (mode === "week" || mode === "by_day") d.setUTCDate(d.getUTCDate() + 7 * delta);
    else d.setUTCMonth(d.getUTCMonth() + delta);
    setAnchor(d);
  };

  const dateLabel = useMemo(() => {
    const FMT_DAY = { day: "numeric", month: "long", year: "numeric", timeZone: PRACTICE_TZ };
    const FMT_SHORT = { month: "short", day: "numeric", timeZone: PRACTICE_TZ };
    const FMT_SHORT_YEAR = { month: "short", day: "numeric", year: "numeric", timeZone: PRACTICE_TZ };
    const FMT_MONTH = { month: "long", year: "numeric", timeZone: PRACTICE_TZ };
    if (mode === "day" || mode === "by_practitioner")
      return anchor.toLocaleDateString(undefined, FMT_DAY);
    if (mode === "week" || mode === "by_day") {
      const s = startOf(anchor, "week");
      const e = new Date(s); e.setUTCDate(e.getUTCDate() + 6);
      return `${s.toLocaleDateString(undefined, FMT_SHORT)} – ${e.toLocaleDateString(undefined, FMT_SHORT_YEAR)}`;
    }
    return anchor.toLocaleDateString(undefined, FMT_MONTH);
  }, [anchor, mode]);

  // Filter by search (client name or service)
  const filtered = useMemo(() => {
    if (!search.trim()) return appts;
    const q = search.toLowerCase();
    return appts.filter((a) => {
      const name = a.client ? `${a.client.first_name} ${a.client.last_initial}.`.toLowerCase() : "";
      const svc = (a.service?.name || "").toLowerCase();
      return name.includes(q) || svc.includes(q);
    });
  }, [appts, search]);

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-sage mb-2">— Schedule</p>
          <h1 className="font-display text-4xl text-charcoal">{title}</h1>
          <p className="text-sm font-ui text-slate mt-1">{subtitle}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 text-slate absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
            <input
              type="search"
              placeholder="Search here…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2.5 bg-white border border-hairline rounded-pill text-sm font-ui text-charcoal w-64 focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/30"
              data-testid="schedule-search"
            />
          </div>
          {showQuickAction && (
            <button
              onClick={() => openNewAppt(null)}
              className="px-5 py-2.5 bg-forest hover:bg-charcoal text-white rounded-pill text-sm font-medium font-ui inline-flex items-center gap-1.5 transition-colors"
              data-testid="schedule-quick-action"
            >
              <Plus className="w-4 h-4" strokeWidth={2} />
              Quick action
            </button>
          )}
        </div>
      </div>

      {/* Calendar card */}
      <div className="bg-white border border-hairline rounded-xl shadow-warm overflow-hidden">
        {/* Calendar header — tabs + date picker */}
        <div className="px-6 py-4 border-b border-hairline flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1 flex-wrap" data-testid="schedule-tabs">
            {[
              { k: "day", label: "Daily" },
              { k: "week", label: "Weekly" },
              { k: "month", label: "Monthly" },
              { k: "by_practitioner", label: "By Practitioner" },
              { k: "by_day", label: "By Day of Week" },
            ].filter(({ k }) => allowedTabs.includes(k)).map(({ k, label }) => (
              <button
                key={k}
                onClick={() => setMode(k)}
                className={`px-4 py-1.5 text-sm font-ui rounded-pill capitalize transition-colors ${
                  mode === k ? "bg-white shadow-warm text-forest" : "text-slate hover:text-forest"
                }`}
                data-testid={`schedule-tab-${k}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button className="p-2 text-slate hover:text-forest hover:bg-sand rounded-md transition-colors" onClick={() => shift(-1)} data-testid="schedule-prev"><ChevronLeft className="w-4 h-4" /></button>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-hairline rounded-pill">
              <CalIcon className="w-3.5 h-3.5 text-slate" strokeWidth={1.5} />
              <span className="text-sm font-ui text-charcoal min-w-[180px] text-center">{dateLabel}</span>
            </div>
            <button className="p-2 text-slate hover:text-forest hover:bg-sand rounded-md transition-colors" onClick={() => shift(1)} data-testid="schedule-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Grid body */}
        <div className="grid lg:grid-cols-12 gap-0">
          <div className="lg:col-span-9 border-r border-hairline">
            {mode === "day" && <DayGrid appts={filtered} anchor={anchor} onRefresh={fetchAppts} onCellClick={enableCellCreate ? openNewAppt : null} onEventClick={handleEventClick} />}
            {mode === "week" && <WeekGrid appts={filtered} anchor={anchor} onRefresh={fetchAppts} onCellClick={enableCellCreate ? openNewAppt : null} onEventClick={handleEventClick} />}
            {mode === "month" && <MonthGrid appts={filtered} anchor={anchor} />}
            {mode === "by_practitioner" && <ByPractitionerGrid appts={filtered} anchor={anchor} practitioners={practitioners} onRefresh={fetchAppts} onCellClick={enableCellCreate ? openNewAppt : null} onEventClick={handleEventClick} />}
            {mode === "by_day" && <ByDayOfWeekGrid appts={filtered} anchor={anchor} practitioners={practitioners} onJumpToDay={(d) => { setAnchor(d); setMode("day"); }} />}
          </div>

          {/* Walk-in side rail */}
          <aside className="lg:col-span-3 p-5 bg-white">
            <div className="border border-hairline rounded-lg p-5" data-testid="walkin-rail">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-sage">Walk-in today</p>
              <p className="text-sm font-ui text-charcoal mt-3">Drop in during open hours — no booking needed.</p>
              <ul className="mt-4 space-y-2.5">
                {walkInServices.map((s) => (
                  <li key={s.id} className="flex items-start gap-2.5">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-pill bg-forest" />
                    <div>
                      <p className="text-sm font-ui text-charcoal">{s.name}</p>
                      <p className="text-xs font-ui text-slate">{s.duration_minutes} min · walk-in only</p>
                    </div>
                  </li>
                ))}
                {walkInServices.length === 0 && <p className="text-sm font-ui text-slate">No walk-in services configured.</p>}
              </ul>
            </div>
          </aside>
        </div>
      </div>

      {/* New appointment dialog */}
      <NewAppointmentDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={() => fetchAppts()}
        defaultStart={dialogStart}
        defaultPractitionerId={dialogPractitionerId}
      />

      <LogVisitDialog
        open={!!logOpenId}
        appointmentId={logOpenId}
        onClose={() => setLogOpenId(null)}
        onSaved={() => fetchAppts()}
      />
    </AdminLayout>
  );
}

// Helper — compute datetime from a UTC anchor date + grid Y offset (px)
function timeFromOffset(anchor, dayDate, offsetPx) {
  const minutes = Math.round((offsetPx / HOUR_HEIGHT) * 60 / 15) * 15; // snap to 15-min
  // dayDate is a practice-TZ midnight Date — start of the day in CT.
  const d = new Date(dayDate);
  // Move forward by DAY_START_HOUR hours + minutes (all in absolute time)
  d.setUTCMinutes(d.getUTCMinutes() + DAY_START_HOUR * 60 + minutes);
  return d;
}

// ---------------- DAY VIEW ----------------
function DayGrid({ appts, anchor, onRefresh, onCellClick, onEventClick }) {
  const dayKey = tzDateKey(anchor);
  const dayAppts = appts.filter((a) => tzDateKey(a.scheduled_start) === dayKey);

  // Lane packing: simple — assign each event to a column if it doesn't overlap
  const lanes = [];
  const ordered = [...dayAppts].sort((a, b) => a.scheduled_start.localeCompare(b.scheduled_start));
  const events = ordered.map((a) => {
    const start = new Date(a.scheduled_start);
    const end = new Date(a.scheduled_end);
    let lane = 0;
    while (true) {
      if (!lanes[lane] || lanes[lane] <= start.getTime()) {
        lanes[lane] = end.getTime();
        break;
      }
      lane++;
    }
    return { ...a, _lane: lane, _start: start, _end: end };
  });
  const cols = Math.max(1, lanes.length, COLS_DAILY);

  const hours = [];
  for (let h = DAY_START_HOUR; h <= DAY_END_HOUR; h++) hours.push(h);

  const handleGridClick = (e) => {
    if (!onCellClick) return;
    if (e.target.closest("[data-testid^='event-']")) return; // ignore event clicks
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const start = timeFromOffset(anchor, anchor, y);
    onCellClick?.(start);
  };

  return (
    <div className="relative px-2 py-4 overflow-x-auto">
      <div className="flex" style={{ minHeight: (DAY_END_HOUR - DAY_START_HOUR) * HOUR_HEIGHT + 40 }}>
        {/* Hour labels */}
        <div className="w-16 shrink-0">
          {hours.map((h) => (
            <div key={h} style={{ height: HOUR_HEIGHT }} className="text-right pr-3 -mt-2 first:mt-0">
              <span className="font-mono text-xs text-slate">{h === 12 ? "12 pm" : h > 12 ? `${h - 12} pm` : `${h} am`}</span>
            </div>
          ))}
        </div>

        {/* Grid + events */}
        <div className={`relative flex-1 border-l border-hairline ${onCellClick ? "cursor-cell" : ""}`} onClick={handleGridClick} data-testid="day-grid">
          {/* Hour lines */}
          {hours.map((h, i) => (
            <div key={h} style={{ height: HOUR_HEIGHT, top: i * HOUR_HEIGHT }} className="border-b border-hairline relative">
              {/* Half-hour divider */}
              <div className="absolute left-0 right-0 top-1/2 border-t border-hairline/50 border-dashed" />
            </div>
          ))}

          {/* Vertical column guides */}
          <div className="absolute inset-0 grid pointer-events-none" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
            {Array.from({ length: cols }).map((_, i) => (
              <div key={i} className={`${i > 0 ? "border-l border-hairline/60" : ""}`} />
            ))}
          </div>

          {/* Events */}
          {events.map((e) => {
            const minutesFromStart = (tzHours(e._start) - DAY_START_HOUR) * 60 + tzMinutes(e._start);
            const duration = (e._end - e._start) / 60000;
            const top = pxFromMinutes(minutesFromStart);
            const height = Math.max(56, pxFromMinutes(duration) - 6);
            const widthPct = 100 / cols;
            const leftPct = (e._lane % cols) * widthPct;
            return (
              <EventCard
                key={e.id}
                appt={e}
                style={{
                  position: "absolute",
                  top,
                  height,
                  left: `calc(${leftPct}% + 6px)`,
                  width: `calc(${widthPct}% - 12px)`,
                }}
                onRefresh={onRefresh}
                onClick={onEventClick}
              />
            );
          })}

          {dayAppts.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <p className="text-sm font-ui text-slate" data-testid="schedule-empty">No appointments yet today — let's schedule one.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------- WEEK VIEW ----------------
function WeekGrid({ appts, anchor, onRefresh, onCellClick, onEventClick }) {
  const start = startOf(anchor, "week");
  const days = Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(start); d.setUTCDate(d.getUTCDate() + i);
    return d;
  });
  const hours = [];
  for (let h = DAY_START_HOUR; h <= DAY_END_HOUR; h++) hours.push(h);

  const handleColClick = (dayDate) => (e) => {
    if (!onCellClick) return;
    if (e.target.closest("[data-testid^='event-']")) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const t = timeFromOffset(dayDate, dayDate, y);
    onCellClick?.(t);
  };

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[900px]">
        {/* Day headers */}
        <div className="grid grid-cols-[64px_repeat(7,1fr)] border-b border-hairline sticky top-0 bg-white z-10">
          <div />
          {days.map((d) => (
            <div key={d.toISOString()} className="px-3 py-3 text-center border-l border-hairline">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate">{d.toLocaleDateString(undefined, { weekday: "short", timeZone: PRACTICE_TZ })}</p>
              <p className="font-ui text-sm text-charcoal mt-0.5">{tzDateKey(d).slice(8)}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-[64px_repeat(7,1fr)]">
          {/* Hour labels */}
          <div>
            {hours.map((h) => (
              <div key={h} style={{ height: HOUR_HEIGHT }} className="text-right pr-3 -mt-2 first:mt-0">
                <span className="font-mono text-xs text-slate">{h === 12 ? "12 pm" : h > 12 ? `${h - 12} pm` : `${h} am`}</span>
              </div>
            ))}
          </div>
          {/* Day columns */}
          {days.map((d) => {
            const dayKey = tzDateKey(d);
            const dayAppts = appts.filter((a) => tzDateKey(a.scheduled_start) === dayKey);
            return (
              <div
                key={dayKey}
                className={`relative border-l border-hairline ${onCellClick ? "cursor-cell" : ""}`}
                style={{ height: (DAY_END_HOUR - DAY_START_HOUR) * HOUR_HEIGHT }}
                onClick={handleColClick(d)}
                data-testid={`week-col-${dayKey}`}
              >
                {hours.map((h, i) => (
                  <div key={h} style={{ height: HOUR_HEIGHT, top: i * HOUR_HEIGHT }} className="border-b border-hairline" />
                ))}
                {dayAppts.map((a) => {
                  const s = new Date(a.scheduled_start);
                  const e = new Date(a.scheduled_end);
                  const minutesFromStart = (tzHours(s) - DAY_START_HOUR) * 60 + tzMinutes(s);
                  const duration = (e - s) / 60000;
                  return (
                    <EventCard
                      key={a.id}
                      appt={a}
                      compact
                      style={{
                        position: "absolute",
                        top: pxFromMinutes(minutesFromStart),
                        height: Math.max(48, pxFromMinutes(duration) - 4),
                        left: 4, right: 4,
                      }}
                      onRefresh={onRefresh}
                      onClick={onEventClick}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------------- MONTH VIEW (simple) ----------------
function MonthGrid({ appts, anchor }) {
  // Walk the month in practice-TZ calendar dates
  const anchorKey = tzDateKey(anchor); // "YYYY-MM-DD" in CT
  const [year, month] = anchorKey.split("-").map(Number);
  const firstOfMonth = tzMidnight(`${year}-${String(month).padStart(2, "0")}-01`);
  const firstWeekday = tzWeekday(firstOfMonth);
  // Determine number of days in the month using a UTC date (length doesn't depend on TZ)
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dKey = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ key: dKey, day: d });
  }
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="p-2">
      <div className="grid grid-cols-7 border-b border-hairline">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
          <div key={d} className="px-3 py-2 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-slate">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((c, i) => {
          if (!c) return <div key={i} className="min-h-[100px] border-r border-b border-hairline bg-sand/30" />;
          const dayAppts = appts.filter((a) => tzDateKey(a.scheduled_start) === c.key);
          return (
            <div key={i} className="min-h-[100px] border-r border-b border-hairline p-2">
              <p className="font-mono text-xs text-charcoal">{c.day}</p>
              <div className="mt-1 space-y-1">
                {dayAppts.slice(0, 3).map((a) => {
                  const v = eventStyleForStatus(a.status);
                  return (
                    <div key={a.id} className={`px-1.5 py-1 rounded-sm text-[10px] font-ui truncate ${v.bg} ${v.text}`} title={`${a.client?.first_name} · ${a.service?.name}`}>
                      {new Date(a.scheduled_start).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", timeZone: PRACTICE_TZ })} {a.client?.first_name} {a.client?.last_initial}.
                    </div>
                  );
                })}
                {dayAppts.length > 3 && <p className="text-[10px] font-ui text-slate">+{dayAppts.length - 3} more</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------- COLUMN GROUP VIEW (shared for By Practitioner / By Room) ----------------
function ColumnGroupGrid({ appts, anchor, columns, getColumnId, emptyHint, onRefresh, onCellClick, onEventClick }) {
  const dayKey = tzDateKey(anchor);
  const dayAppts = appts.filter((a) => tzDateKey(a.scheduled_start) === dayKey);
  const hours = [];
  for (let h = DAY_START_HOUR; h <= DAY_END_HOUR; h++) hours.push(h);

  // Group events by column id
  const byCol = new Map();
  columns.forEach((c) => byCol.set(c.id, []));
  // Ensure an "Unassigned" bucket for appts whose column id isn't in columns
  const UNASSIGNED = "__unassigned";
  if (!byCol.has(UNASSIGNED)) byCol.set(UNASSIGNED, []);
  dayAppts.forEach((a) => {
    const cid = getColumnId(a);
    if (cid && byCol.has(cid)) byCol.get(cid).push(a);
    else byCol.get(UNASSIGNED).push(a);
  });

  const allCols = [...columns];
  if (byCol.get(UNASSIGNED).length) {
    allCols.push({ id: UNASSIGNED, label: "Unassigned" });
  }

  if (allCols.length === 0) {
    return (
      <div className="px-6 py-16 text-center text-sm font-ui text-slate" data-testid="column-group-empty">
        {emptyHint || "No columns to display."}
      </div>
    );
  }

  const handleColClick = (col) => (e) => {
    if (!onCellClick) return;
    if (e.target.closest("[data-testid^='event-']")) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const t = timeFromOffset(anchor, anchor, y);
    // Forward extra context if practitioner column
    if (col._practitionerId) onCellClick?.(t, col._practitionerId);
    else onCellClick?.(t);
  };

  const colCount = allCols.length;

  return (
    <div className="overflow-x-auto">
      <div style={{ minWidth: Math.max(720, colCount * 200 + 64) }}>
        {/* Column headers */}
        <div className="grid border-b border-hairline sticky top-0 bg-white z-10" style={{ gridTemplateColumns: `64px repeat(${colCount}, minmax(0, 1fr))` }}>
          <div />
          {allCols.map((c) => (
            <div key={c.id} className="px-3 py-3 text-center border-l border-hairline">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate">{c.eyebrow || ""}</p>
              <p className="font-ui text-sm text-charcoal mt-0.5">{c.label}</p>
            </div>
          ))}
        </div>

        <div className="grid" style={{ gridTemplateColumns: `64px repeat(${colCount}, minmax(0, 1fr))` }}>
          {/* Hour labels */}
          <div>
            {hours.map((h) => (
              <div key={h} style={{ height: HOUR_HEIGHT }} className="text-right pr-3 -mt-2 first:mt-0">
                <span className="font-mono text-xs text-slate">{h === 12 ? "12 pm" : h > 12 ? `${h - 12} pm` : `${h} am`}</span>
              </div>
            ))}
          </div>
          {/* Columns */}
          {allCols.map((col) => {
            const colAppts = byCol.get(col.id) || [];
            return (
              <div
                key={col.id}
                className={`relative border-l border-hairline ${onCellClick ? "cursor-cell" : ""}`}
                style={{ height: (DAY_END_HOUR - DAY_START_HOUR) * HOUR_HEIGHT }}
                onClick={handleColClick(col)}
                data-testid={`column-${col.id}`}
              >
                {hours.map((h, i) => (
                  <div key={h} style={{ height: HOUR_HEIGHT, top: i * HOUR_HEIGHT }} className="border-b border-hairline" />
                ))}
                {colAppts.map((a) => {
                  const s = new Date(a.scheduled_start);
                  const e = new Date(a.scheduled_end);
                        const minutesFromStart = (tzHours(s) - DAY_START_HOUR) * 60 + tzMinutes(s);
                  const duration = (e - s) / 60000;
                  return (
                    <EventCard
                      key={a.id}
                      appt={a}
                      style={{
                        position: "absolute",
                        top: pxFromMinutes(minutesFromStart),
                        height: Math.max(56, pxFromMinutes(duration) - 4),
                        left: 4, right: 4,
                      }}
                      onRefresh={onRefresh}
                      onClick={onEventClick}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------------- BY PRACTITIONER ----------------
function ByPractitionerGrid({ appts, anchor, practitioners, onRefresh, onCellClick, onEventClick }) {
  const active = practitioners.filter((p) => p.active !== false);
  const columns = active.map((p) => ({
    id: p.id,
    label: p.display_name,
    eyebrow: "Practitioner",
    _practitionerId: p.id,
  }));
  return (
    <ColumnGroupGrid
      appts={appts}
      anchor={anchor}
      columns={columns}
      getColumnId={(a) => a.practitioner_id}
      emptyHint="No practitioners configured."
      onRefresh={onRefresh}
      onCellClick={onCellClick}
      onEventClick={onEventClick}
    />
  );
}

// ---------------- BY DAY OF WEEK (overview) ----------------
const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const WEEKDAY_LABELS = {
  sun: "Sunday", mon: "Monday", tue: "Tuesday", wed: "Wednesday",
  thu: "Thursday", fri: "Friday", sat: "Saturday",
};

function ByDayOfWeekGrid({ appts, anchor, practitioners, onJumpToDay }) {
  // Build a week starting Sunday, anchored to the current week
  const weekStart = startOf(anchor, "week");
  const days = Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(weekStart);
    d.setUTCDate(d.getUTCDate() + i);
    return d;
  });

  const active = practitioners.filter((p) => p.active !== false);

  return (
    <div className="p-5">
      <div className="mb-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-sage">— Week at a glance</p>
        <p className="text-sm font-ui text-slate mt-1">Each day shows the practitioner on duty. Click a day to see its full schedule.</p>
      </div>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4" data-testid="by-day-grid">
        {days.map((d, idx) => {
          const wd = WEEKDAY_KEYS[tzWeekday(d)];
          const dayKey = tzDateKey(d);
          const onDuty = active.find((p) => (p.working_days || []).includes(wd));
          const dayAppts = appts.filter((a) => tzDateKey(a.scheduled_start) === dayKey);
          const isToday = dayKey === tzDateKey(new Date());
          return (
            <button
              key={dayKey}
              onClick={() => onJumpToDay?.(d)}
              className={`text-left bg-white border ${isToday ? "border-forest" : "border-hairline"} rounded-lg p-4 hover:border-forest hover:shadow-warm transition-all`}
              data-testid={`by-day-card-${wd}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate">{WEEKDAY_LABELS[wd]}</p>
                  <p className="font-display text-2xl text-charcoal mt-0.5">{tzDateKey(d).slice(8)}</p>
                  <p className="font-mono text-[10px] text-slate">{d.toLocaleDateString(undefined, { month: "short", timeZone: PRACTICE_TZ })}</p>
                </div>
                {isToday && (
                  <span className="px-2 py-0.5 rounded-pill bg-forest text-white text-[10px] font-ui font-medium">Today</span>
                )}
              </div>
              {onDuty ? (
                <div className="flex items-center gap-2.5 pt-3 border-t border-hairline">
                  {onDuty.photo_url ? (
                    <img src={onDuty.photo_url} alt="" className="w-9 h-9 rounded-pill object-cover" />
                  ) : (
                    <div className="w-9 h-9 rounded-pill bg-cream flex items-center justify-center text-forest font-medium text-sm">
                      {onDuty.display_name?.[0]}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-sage">On duty</p>
                    <p className="text-sm font-ui text-charcoal truncate">{onDuty.display_name}</p>
                  </div>
                </div>
              ) : (
                <div className="pt-3 border-t border-hairline">
                  <p className="text-xs font-ui text-slate italic">Closed — no practitioner scheduled.</p>
                </div>
              )}
              <p className="mt-3 text-xs font-ui text-slate">
                {dayAppts.length === 0
                  ? "No appointments yet."
                  : `${dayAppts.length} appointment${dayAppts.length === 1 ? "" : "s"} booked.`}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------- EVENT CARD ----------------
function EventCard({ appt, style, compact, onRefresh, onClick }) {
  const v = eventStyleForStatus(appt.status);
  const start = new Date(appt.scheduled_start);
  const end = new Date(appt.scheduled_end);
  const time = `${start.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", timeZone: PRACTICE_TZ })} – ${end.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", timeZone: PRACTICE_TZ })}`;
  const duration = Math.round((end - start) / 60000);
  const initials = appt.client ? `${appt.client.first_name?.[0] || ""}${appt.client.last_initial?.[0] || ""}` : "—";

  const checkin = async (e) => { e.stopPropagation(); await api.post(`/appointments/${appt.id}/check-in`); onRefresh?.(); };
  const handleCardClick = (e) => {
    if (!onClick) return;
    // Don't fire when clicking inner buttons/links
    if (e.target.closest("button, a")) return;
    onClick(appt);
  };

  return (
    <div
      style={style}
      onClick={handleCardClick}
      className={`group rounded-lg border-l-4 ${v.border} ${v.bg} ${v.text} shadow-warm overflow-hidden flex flex-col ${onClick ? "cursor-pointer" : ""}`}
      data-testid={`event-${appt.id}`}
    >
      <div className={`p-2.5 flex items-start gap-2 ${compact ? "" : "lg:p-3"}`}>
        <div className={`w-6 h-6 rounded-pill flex items-center justify-center text-[10px] font-medium shrink-0 ${v.filled ? "bg-white/20 text-white" : "bg-white text-forest border border-hairline"}`}>
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-xs font-ui font-semibold truncate ${v.filled ? "text-white" : "text-charcoal"}`}>
            {appt.client ? `${appt.client.first_name} ${appt.client.last_initial}.` : "—"}
          </p>
          <p className={`text-[11px] font-ui truncate ${v.filled ? "text-white/80" : "text-slate"}`}>
            {appt.service?.name || ""}
          </p>
          {!compact && (
            <p className={`text-[10px] font-mono mt-1 ${v.filled ? "text-white/70" : "text-slate"}`}>
              {time} <span className="opacity-70">({duration} min)</span>
            </p>
          )}
        </div>
        <button className={`shrink-0 p-1 rounded-sm ${v.filled ? "text-white/70 hover:text-white" : "text-slate hover:text-forest"} opacity-0 group-hover:opacity-100 transition-opacity`} aria-label="More">
          <MoreVertical className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="px-2.5 pb-2 mt-auto flex items-center justify-between gap-2">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-pill text-[10px] font-medium font-ui ${v.chip}`}>
          {statusLabel(appt.status)}
        </span>
        <div className="flex items-center gap-2">
          {appt.status === "confirmed" && !compact && (
            <button onClick={checkin} className={`text-[10px] font-ui px-2 py-0.5 rounded-sm ${v.filled ? "bg-white/20 text-white hover:bg-white/30" : "bg-white text-forest border border-hairline hover:border-forest"}`} data-testid={`event-checkin-${appt.id}`}>Check in</button>
          )}
          {appt.client_id && (
            <Link to={`/admin/clients/${appt.client_id}`} className={`text-[10px] font-ui ${v.filled ? "text-white/80 hover:text-white" : "text-sage hover:text-forest"}`}>Open →</Link>
          )}
        </div>
      </div>
    </div>
  );
}
